import React from 'react';
import { createPortal } from 'react-dom';
import { X, MessageSquareHeart, ThumbsUp, ThumbsDown } from 'lucide-react';

interface FeedbackRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAcknowledge: () => void;
}

/**
 * One-time popup modal requesting users to provide feedback via thumbs up/down
 * This modal is shown once per user (tracked via localStorage)
 */
const FeedbackRequestModal: React.FC<FeedbackRequestModalProps> = ({ 
  isOpen, 
  onClose, 
  onAcknowledge 
}) => {
  if (!isOpen) return null;

  const handleAcknowledge = () => {
    onAcknowledge();
    onClose();
  };

  return createPortal(
    <div 
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-request-title"
    >
      <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-800 flex items-center justify-center">
              <MessageSquareHeart className="w-5 h-5 text-teal-600 dark:text-teal-300" />
            </div>
            <h3 id="feedback-request-title" className="text-lg font-semibold text-gray-900 dark:text-white">
              Help Us Improve Scout Central
            </h3>
          </div>
          <button 
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" 
            onClick={onClose} 
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-4">
          <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed text-center">
            After each response, rate it with a thumbs up or down. 
            Your feedback helps us improve Scout Central!
          </p>

          {/* Thumbs icons display */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 px-4 py-5">
            <div className="flex items-center justify-center gap-6 mb-3">
              <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm">
                <ThumbsUp className="w-5 h-5 text-gray-400" />
              </div>
              <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm">
                <ThumbsDown className="w-5 h-5 text-gray-400" />
              </div>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
              Look for these icons at the bottom of each response
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-center">
          <button
            type="button"
            className="px-6 py-2.5 rounded-xl text-sm bg-teal-500 hover:bg-teal-600 text-white font-medium transition-colors shadow-sm"
            onClick={handleAcknowledge}
          >
            Got It!
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FeedbackRequestModal;

